import React, { useState, useMemo, useEffect } from "react";
import { Button, Form, Row, Col, Spinner } from "react-bootstrap";
import { Formik, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import DatePicker from "react-datepicker";
import callApi from "../../helpers/CallApi";
import { DATE_FORMAT, formatApiDate } from "../../helpers/dateFormat";
import {
  getBankAccountList,
  getExpenseCategory,
  createExpenseCategory,
  createExpense,
  getTotalBalance,
  getExpense,
  updateExpense,
} from "../../helpers/API";
import { formatINR } from "../../helpers";
import { toast } from "react-toastify";
import useApi from "../../Hooks/useApi";

const defaultFormValues = {
  expenseCategory: "",
  date: new Date(),
  description: "",
  amount: "",
  paymentType: "cash",
  bankAccountId: "",
  chequeNumber: "",
};

function normalizeExpenseResponse(res) {
  if (!res) return null;
  if (res.data && typeof res.data === "object" && !Array.isArray(res.data)) {
    return res.data;
  }
  return res;
}

function mapExpenseToFormValues(expense) {
  if (!expense) return { ...defaultFormValues };
  const catId =
    expense.expenseCategory ?? expense.categoryId ?? expense.expenseCategoryId ?? "";
  const bankId = expense.bankAccountId ?? expense.bankAccount ?? "";
  let date = new Date();
  if (expense.date) {
    try {
      date = new Date(expense.date);
    } catch {
      date = new Date();
    }
  }
  return {
    expenseCategory: catId !== "" && catId != null ? String(catId) : "",
    date,
    description: expense.description ?? "",
    amount:
      expense.amount != null && expense.amount !== "" ? String(expense.amount) : "",
    paymentType: expense.paymentType === "bank" ? "bank" : "cash",
    bankAccountId: bankId !== "" && bankId != null ? String(bankId) : "",
    chequeNumber: expense.chequeNumber ?? "",
  };
}

const AddExpenseModal = ({
  show,
  toggle,
  editingExpenseId = null,
}) => {
  const { t } = useTranslation();
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [totalCashBalance, setTotalCashBalance] = useState(null);
  const [totalBankBalance, setTotalBankBalance] = useState(null);
  const [expenseForEdit, setExpenseForEdit] = useState(null);

  const isEditMode =
    editingExpenseId !== null && editingExpenseId !== undefined && editingExpenseId !== "";

  useEffect(() => {
    setExpenseForEdit(null);
  }, [editingExpenseId, show]);

  const [expenseLoad] = useApi({
    callback: getExpense,
    payload: [editingExpenseId],
    dependencies: [show, editingExpenseId],
    shouldCallApi: show && isEditMode,
    onSuccess: (res) => {
      const raw = normalizeExpenseResponse(res);
      if (!raw || typeof raw !== "object") {
        toast.error(t("expenses.form.messages.loadExpenseError"));
        toggle();
        return;
      }
      setExpenseForEdit(raw);
    },
    onError: () => {
      toast.error(t("expenses.form.messages.loadExpenseError"));
      toggle();
    },
  });

  useApi({
    callback: getBankAccountList,
    payload: [{ pagination: false }],
    dependencies: [show],
    shouldCallApi: show,
    onSuccess: (res) => setBankAccounts(res?.data || []),
  });
  const [totalBalanceApi] = useApi({
    callback: getTotalBalance,
    payload: [],
    dependencies: [show],
    shouldCallApi: show,
    onSuccess: (res) => {
      setTotalCashBalance(res?.cashBalance?.[0]?.totalBalance ?? null);
      setTotalBankBalance(res?.bankBalance?.[0]?.totalBalance ?? null);
    },
  });
  useApi({
    callback: getExpenseCategory,
    payload: [{ pagination: false }],
    dependencies: [show, showAddCategoryModal],
    shouldCallApi: show,
    onSuccess: (res) => setExpenseCategories(res?.data || []),
  });

  const initialValues = useMemo(
    () => {
      if (expenseForEdit) {
        return mapExpenseToFormValues(expenseForEdit);
      }
      return { ...defaultFormValues, date: new Date() };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `show` refreshes add defaults when the modal reopens
    [expenseForEdit, show]
  );

  const validationSchema = Yup.object({
    expenseCategory: Yup.string().required(t("expenses.form.validation.expenseCategoryRequired")),
    date: Yup.date().required(t("expenses.form.validation.dateRequired")),
    amount: Yup.number()
      .min(0.01, t("expenses.form.validation.amountMin"))
      .required(t("expenses.form.validation.amountRequired")),
    paymentType: Yup.string().oneOf(["cash", "bank"]).required(),
    bankAccountId: Yup.string().when("paymentType", {
      is: "bank",
      then: (schema) => schema.required(t("expenses.form.validation.bankRequired")),
    }),
    chequeNumber: Yup.string().when("paymentType", {
      is: "bank",
      then: (schema) => schema.required(t("expenses.form.validation.chequeNumberRequired")),
    }),
  });

  const handleSubmit = (values) => {
    const payload = {
      expenseCategory: values.expenseCategory,
      date: formatApiDate(values.date),
      amount: Number(values.amount) || 0,
      paymentType: values.paymentType,
      description: values.description,
    };
    if (values.paymentType === "bank") {
      payload.bankAccountId = values.bankAccountId;
      payload.chequeNumber = values.chequeNumber;
    }
    if (isEditMode) {
      callApi({
        callback: updateExpense,
        payload: [editingExpenseId, payload],
        onSuccess: () => {
          toast.success(t("expenses.form.messages.updateSuccess"));
          toggle();
        },
        onError: (msg) => {
          toast.error(msg || t("expenses.form.messages.updateError"));
        },
      });
      return;
    }
    callApi({
      callback: createExpense,
      payload: [payload],
      onSuccess: () => {
        toast.success(t("expenses.form.messages.createSuccess"));
        toggle();
      },
      onError: (msg) => {
        toast.error(msg || t("expenses.form.messages.createError"));
        toggle();
      },
    });
  };

  const showEditLoader = isEditMode && expenseLoad.loading === true && !expenseForEdit;

  const handleAddCategory = () => {
    const name = (newCategoryName || "").trim();
    if (!name) return;
    callApi({
      callback: createExpenseCategory,
      payload: [{ categoryName: name }],
      onSuccess: () => {
        toast.success(t("expenses.form.messages.categoryAdded"));
        setNewCategoryName("");
        setShowAddCategoryModal(false);
      },
      onError: (msg) => {
        toast.error(msg || t("expenses.form.messages.categoryAddError"));
      },
    });
  };

  return (
    <>
      <Modal isOpen={show} toggle={toggle} size="lg" centered>
        <ModalHeader toggle={toggle}>
          {isEditMode ? t("expenses.form.titleEdit") : t("expenses.form.title")}
        </ModalHeader>
        <ModalBody>
          {showEditLoader ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted small mt-2 mb-0">{t("expenses.form.loadingExpense")}</p>
            </div>
          ) : (
          <Formik
            key={show ? `${isEditMode ? editingExpenseId : "add"}-${expenseForEdit?.id ?? "n"}` : "closed"}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
              setFieldTouched,
              resetForm,
            }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="required">{t("expenses.form.expenseCategory")}</Form.Label>
                      <div className="d-flex gap-2 align-items-start">
                        <Form.Select
                          name="expenseCategory"
                          value={values.expenseCategory}
                          onChange={(e) => setFieldValue("expenseCategory", e.target.value)}
                          onBlur={handleBlur}
                          isInvalid={touched.expenseCategory && !!errors.expenseCategory}
                        >
                          <option value="">{t("expenses.form.selectExpenseCategory")}</option>
                          {expenseCategories.map((category) => (
                            <option key={category?.id} value={category?.id}>
                              {category?.name}
                            </option>
                          ))}
                        </Form.Select>
                        <Button
                          type="button"
                          variant="outline-primary"
                          className="px-2"
                          title={t("expenses.form.addNewCategory")}
                          onClick={() => setShowAddCategoryModal(true)}
                        >
                          <i className="bx bx-plus" style={{ fontSize: "1.25rem" }}></i>
                        </Button>
                      </div>
                      <ErrorMessage
                        name="expenseCategory"
                        component="div"
                        className="text-danger small mt-1"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="required">{t("expenses.form.date")}</Form.Label>
                      <DatePicker
                        wrapperClassName="w-100"
                        selected={values.date}
                        onChange={(date) => setFieldValue("date", date)}
                        dateFormat={DATE_FORMAT.DISPLAY}
                        className={`form-control ${touched.date && errors.date ? "is-invalid" : ""}`}
                      />
                      <ErrorMessage name="date" component="div" className="text-danger small mt-1" />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">

                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>{t("expenses.form.description")}</Form.Label>
                      <Form.Control
                        type="text"
                        name="description"
                        as="textarea"
                        rows={3}
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={t("expenses.form.placeholders.description")}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="required">{t("expenses.form.amount")}</Form.Label>
                      <Form.Control
                        type="number"
                        name="amount"
                        value={values.amount}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={t("expenses.form.placeholders.amount")}
                        step="0.01"
                        min="0"
                        isInvalid={touched.amount && !!errors.amount}
                      />
                      <ErrorMessage name="amount" component="div" className="text-danger small mt-1" />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="required">{t("expenses.form.paymentType")}</Form.Label>
                      <div>
                        <input
                          type="radio"
                          id="paymentType-cash"
                          name="paymentType"
                          value="cash"
                          checked={values.paymentType === "cash"}
                          onClick={() => {
                            setFieldValue("paymentType", "cash", true);
                            setFieldTouched("paymentType", true, true);
                          }}
                          onBlur={() => setFieldTouched("paymentType", true, true)}
                          className={`form-check-input me-2 ${touched.paymentType && errors.paymentType ? "is-invalid" : ""}`}
                        />
                        <label htmlFor="paymentType-cash" className="form-check-label me-3">
                          {t("expenses.form.paymentTypeCash")}
                        </label>
                        <input
                          type="radio"
                          id="paymentType-bank"
                          name="paymentType"
                          value="bank"
                          checked={values.paymentType === "bank"}
                          onClick={() => {
                            setFieldValue("paymentType", "bank", true);
                            setFieldTouched("paymentType", true, true);
                          }}
                          onBlur={() => setFieldTouched("paymentType", true, true)}
                          className={`form-check-input me-2 ${touched.paymentType && errors.paymentType ? "is-invalid" : ""}`}
                        />
                        <label htmlFor="paymentType-bank" className="form-check-label">
                          {t("expenses.form.paymentTypeBank")}
                        </label>
                      </div>
                      <div className="mt-2 pt-1 border-top border-light">
                        {totalBalanceApi.loading ? (
                          <div className="d-flex align-items-center gap-2 text-muted small">
                            <Spinner animation="border" size="sm" variant="secondary" />
                            <span>{t("expenses.form.balanceLoading")}</span>
                          </div>
                        ) : totalBalanceApi.error ? (
                          <span className="text-muted small">{t("expenses.form.balanceUnavailable")}</span>
                        ) : values.paymentType === "cash" ? (
                          <div className="small">
                            <span className="text-muted">{t("expenses.form.cashBalance")}</span>
                            <span className="fw-semibold text-dark ms-2">
                              {formatINR(Number(totalCashBalance ?? 0))}
                            </span>
                          </div>
                        ) : (
                          <div className="small">
                            <span className="text-muted">{values.bankAccountId ? t("expenses.form.bankBalance") : t("expenses.form.totalBalance")}</span>
                            <span className="fw-semibold text-dark ms-2">
                              {formatINR(Number(totalBankBalance ?? 0))}
                            </span>
                          </div>
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
                {values.paymentType === "bank" && (
                  <>

                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="required">{t("expenses.form.bank")}</Form.Label>
                          <Form.Select
                            name="bankAccountId"
                            value={values.bankAccountId}
                            onChange={(e) => {
                              const id = e.target.value;
                              setFieldValue("bankAccountId", id);
                              setTotalBankBalance(
                                bankAccounts.find((acc) => String(acc.id) === String(id))?.balance ?? null
                              );
                            }}
                            onBlur={handleBlur}
                            isInvalid={touched.bankAccountId && !!errors.bankAccountId}
                          >
                            <option value="">{t("expenses.form.selectBank")}</option>
                            {bankAccounts?.map((acc) => (
                              <option key={acc.id} value={acc.id}>
                                {acc.bankName || acc.accountHolderName || acc.accountNumber} - {acc.accountNumber || ""}
                              </option>
                            ))}
                          </Form.Select>
                          <ErrorMessage
                            name="bankAccountId"
                            component="div"
                            className="text-danger small mt-1"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="required">{t("expenses.form.chequeNumber")}</Form.Label>
                          <Form.Control
                            type="text"
                            name="chequeNumber"
                            value={values.chequeNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder={t("expenses.form.placeholders.chequeNumber")}
                            isInvalid={touched.chequeNumber && !!errors.chequeNumber}
                          />
                          <ErrorMessage
                            name="chequeNumber"
                            component="div"
                            className="text-danger small mt-1"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </>
                )}

                <ModalFooter className="p-0 pt-3 border-0">
                  <Button type="button" variant="secondary" onClick={() => resetForm()}>
                    {t("expenses.form.reset")}
                  </Button>
                  <Button type="button" variant="outline-secondary" onClick={toggle}>
                    {t("expenses.form.cancel")}
                  </Button>
                  <Button type="submit" variant="primary">
                    {t("expenses.form.save")}
                  </Button>
                </ModalFooter>
              </Form>
            )}
          </Formik>
          )}
        </ModalBody>
      </Modal>

      <Modal isOpen={showAddCategoryModal} toggle={() => setShowAddCategoryModal(false)} size="sm" centered>
        <ModalHeader toggle={() => setShowAddCategoryModal(false)}>
          {t("expenses.addCategoryModal.title")}
        </ModalHeader>
        <ModalBody>
          <Form.Group>
            <Form.Label>{t("expenses.addCategoryModal.name")}</Form.Label>
            <Form.Control
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder={t("expenses.addCategoryModal.placeholder")}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddCategory())}
            />
          </Form.Group>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowAddCategoryModal(false)}>
            {t("expenses.addCategoryModal.cancel")}
          </Button>
          <Button variant="primary" onClick={handleAddCategory} disabled={!newCategoryName.trim()}>
            {t("expenses.addCategoryModal.save")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default AddExpenseModal;
